import dayjs from 'dayjs';
import _ from 'lodash';
import { all, delay, fork, put, takeLeading } from 'redux-saga/effects';
import ApiConstants from 'src/common/helpers/ApiConstants';
import { toastError, toastWarning } from 'src/common/helpers/Notifications';
import TextConstants from 'src/common/helpers/TextConstants';
import {
  chunkArray,
  downsampleData,
  fetchCombinedDataParallellyAsync,
  fetchDataParallellyAsync,
  isInAbnormalRange,
} from 'src/common/helpers/Utils';
import * as requests from '../../helpers/HTTPRequest';
import * as Actions from './actions';

const MAX_GRAPH_RECORDS = 4000;
const MAX_SEQUENCE_NO = 53196;

function* fetchGraphDataSaga({ payload, callback }) {
  try {
    const { devices, startTime, endTime } = payload;

    let startDatetime = new Date(startTime);
    let endDatetime = new Date(endTime);
    let startISOStr = dayjs(
      startDatetime.setUTCHours(startDatetime.getUTCHours() + 9)
    )
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');
    let endISOStr = dayjs(
      endDatetime.setUTCHours(endDatetime.getUTCHours() + 9)
    )
      .toISOString()
      .replace(/\.\d+Z$/, '+09:00');

    let containsTodayData = false;
    const tasks = [];
    for (const { deviceId } of devices) {
      const combinedUrl = `${ApiConstants.BASE_URL}${ApiConstants.CSV_COMBINED_DATA}/${deviceId}?startTime=${startISOStr}&endTime=${endISOStr}`;

      tasks[deviceId] = [
        fetchCombinedDataParallellyAsync(requests.getRequest, combinedUrl),
      ];

      // fetch today data in case the time range contains today time
      const today = new Date();
      const todayStart = dayjs(today).startOf('D');
      if (dayjs(endDatetime).valueOf() >= todayStart.valueOf()) {
        let todayStartTime = dayjs(today).format('YYYY-MM-DD 00:00');

        if (dayjs(startDatetime).isAfter(todayStart)) {
          todayStartTime = startTime;
        }

        const todayUrl = `${ApiConstants.BASE_URL}${ApiConstants.CSV_DATA_MANAGEMENT}/${deviceId}?startTime=$start$&endTime=$end$`;

        tasks[deviceId].push(
          fetchDataParallellyAsync(
            requests.getRequest,
            todayUrl,
            dayjs(todayStartTime),
            dayjs(endTime),
            60
          )
        );

        containsTodayData = true;
      }
      //
    }

    // waiting for all tasks done
    let responses = yield all(Object.values(tasks).flat());
    if (containsTodayData && responses?.length) {
      // 2 is combined data and today data
      responses = chunkArray(responses, 2);
    }

    // process the data locally
    const graphDataSets = {};
    devices.forEach(({ deviceId, channels }, index) => {
      let records = [];
      const target = responses[index] || [];

      for (let i = 0; i < target.length; i++) {
        const item = target[i];

        if (Array.isArray(item)) {
          for (let j = 0; j < item.length; j++) {
            records.push(item[j]);
          }
        } else {
          records.push(item);
        }
      }

      // filter by start and end time & sno !=0 0
      records = records.filter(
        (x) =>
          x.sno !== 0 &&
          dayjs(x.lts) >= dayjs(startTime) &&
          dayjs(x.lts) <= dayjs(endTime)
      );

      // format datetime
      records.forEach((record) => {
        record.lts = dayjs(record.lts).format('YYYY/MM/DD HH:mm:ss');
      });

      // unique by time
      records = _.uniqBy(records, 'lts');

      // records are sorted by time and sequence number
      records = _.orderBy(records, ['lts', 'sno'], ['asc', 'asc']);

      // only number channels
      const validChannels = channels.filter(
        (c) => [0, 1, 2].includes(c.dataType) && !!c.dataUnit
      );

      // TODO should map channel by sensor type instead
      // mapped channels records: {channelId: { timestamp: value }}
      const mappedRecords = Object.fromEntries(
        validChannels.map(({ channelId }) => [channelId, {}])
      );
      // mapped channels alarm count: {channelId: 0}
      const mappedAlarms = Object.fromEntries(
        validChannels.map(({ channelId }) => [channelId, 0])
      );

      const _addChannelsData = (record, isMissing = false) => {
        /**
         * Define inner function to add channels data
         * Params:
         *  `record` will be prev point if current is missing point
         *  `isMissing｀ is the flag for adding missing point
         * */

        // keep the value as null for abnormal or missing values to break the graph line
        if (mappedRecords['CH1']) {
          const tsp = !isMissing
            ? record.lts
            : dayjs(record.lts).add(1, 'second').format('YYYY/MM/DD HH:mm:ss');
          mappedRecords['CH1'][tsp] =
            !isInAbnormalRange(record.ch1) && !isMissing ? record.ch1 : NaN;
        }
        if (mappedRecords['CH2']) {
          const tsp = !isMissing
            ? record.lts
            : dayjs(record.lts).add(1, 'second').format('YYYY/MM/DD HH:mm:ss');
          mappedRecords['CH2'][tsp] =
            !isInAbnormalRange(record.ch2) && !isMissing ? record.ch2 : NaN;
        }
        if (mappedRecords['CH3']) {
          const tsp = !isMissing
            ? record.lts
            : dayjs(record.lts).add(1, 'second').format('YYYY/MM/DD HH:mm:ss');
          mappedRecords['CH3'][tsp] =
            !isInAbnormalRange(record.ch3) && !isMissing ? record.ch3 : NaN;
        }
        if (mappedRecords['CH4']) {
          const tsp = !isMissing
            ? record.lts
            : dayjs(record.lts).add(1, 'second').format('YYYY/MM/DD HH:mm:ss');
          mappedRecords['CH4'][tsp] =
            !isInAbnormalRange(record.ch4) && !isMissing ? record.ch4 : NaN;
        }
      };

      const _addChannelAlarmCount = (record) => {
        /**
         * Define inner function to count channel alarm flag
         * An alarm will be counted if CH measure value in abnormal range or CH alarm flag contains '2'
         * Params:
         *  `record` measurement record (L2/G1/L7)
         * */

        const isNumber = (value) =>
          typeof value === 'number' && !Number.isNaN(value);

        if (
          isNumber(mappedAlarms['CH1']) &&
          ((record.af1 && String(record.af1).includes('2')) ||
            isInAbnormalRange(record.ch1))
        ) {
          mappedAlarms['CH1'] += 1;
        }
        if (
          isNumber(mappedAlarms['CH2']) &&
          ((record.af2 && String(record.af2).includes('2')) ||
            isInAbnormalRange(record.ch2))
        ) {
          mappedAlarms['CH2'] += 1;
        }
        if (
          isNumber(mappedAlarms['CH3']) &&
          ((record.af3 && String(record.af3).includes('2')) ||
            isInAbnormalRange(record.ch3))
        ) {
          mappedAlarms['CH3'] += 1;
        }
        if (
          isNumber(mappedAlarms['CH4']) &&
          ((record.af4 && String(record.af4).includes('2')) ||
            isInAbnormalRange(record.ch4))
        ) {
          mappedAlarms['CH4'] += 1;
        }
      };

      // build the output object
      // count the number of missing points and alarms
      let previous = null;
      let missing = 0;
      records.forEach((record) => {
        // this will skip first time
        if (previous !== null) {
          // if the sequence number of this record isn't incremental, count number of missing samples
          // iterate from previous to current until the sequence number matches
          const target = record['sno'];
          let current = previous['sno'];
          // TODO needs a stronger safety check for inf loop
          if (
            false ===
            (target >= 1 &&
              target <= MAX_SEQUENCE_NO &&
              current >= 1 &&
              current <= MAX_SEQUENCE_NO)
          ) {
            // broken sequence number
          } else {
            if (current + 1 !== target) {
              while (current + 1 !== target) {
                // generate a dummy record for missing sample and go to next sequence number (with wrap)
                // don't forget that sequence number starts at 1, not 0
                current += 1;
                // not count missing for abnormal point
                if (current <= MAX_SEQUENCE_NO) {
                  missing += 1;
                  _addChannelsData(previous, true);
                } else {
                  current = 0; // this is fine because the tests are (+1)
                }
              }
            }
          }
        }
        previous = record;

        _addChannelsData(record);
        _addChannelAlarmCount(record);
      });

      Object.entries(mappedRecords).forEach(([channelId, mappedPoints]) => {
        const channel = validChannels.find((c) => c.channelId === channelId);
        const channelUnit = channel.dataUnit;
        const channelKey = `${deviceId}_${channelId}`;

        // init the default object for type of graph (channelUnit.channelKey)
        if (!graphDataSets[channelUnit]) {
          graphDataSets[channelUnit] = {};
        }
        if (!graphDataSets[channelUnit][channelKey]) {
          // remove null and NaN point values
          const validValues = Object.values(mappedPoints).filter(
            (x) => ![NaN, null].includes(x)
          );

          // calculate min, max and total
          let minValue = null;
          let maxValue = null;
          let avgValue = null;
          let totalValue = 0;

          if (validValues.length) {
            minValue = validValues[0];
            maxValue = validValues[0];
            totalValue = validValues[0];

            for (let i = 1; i < validValues.length; i++) {
              const val = validValues[i];
              if (val < minValue) minValue = val;
              if (val > maxValue) maxValue = val;
              totalValue += val;
            }

            avgValue = totalValue / validValues.length;
          }
          // end calculation

          // set graph data set for logger channel based on data unit
          graphDataSets[channelUnit][channelKey] = {
            records: [],
            metaData: {
              missing,
              minValue,
              maxValue,
              avgValue,
              numOfRecords: validValues.length,
              numOfAlarms: mappedAlarms[channelId] || 0, // this always has value of channelId
            },
          };
        }

        let points = Object.entries(mappedPoints).map(([timestamp, value]) => ({
          timestamp,
          value,
        }));

        let w = null;
        if (points.length > MAX_GRAPH_RECORDS) {
          const result = downsampleData(points, MAX_GRAPH_RECORDS);
          points = result.downsampledData;
          w = result.w;
        }

        graphDataSets[channelUnit]['windowSize'] = w;

        points.forEach((point) => {
          const t = new Date(point.timestamp);
          const value =
            point.value !== null ? parseFloat(Number(point.value)) : null;
          graphDataSets[channelUnit][channelKey]['records'].push([
            new Date(t),
            value,
          ]);
        });
      });
    });

    const hasGraphData = (data) =>
      Object.values(data).some((unitData) =>
        Object.values(unitData).some(
          (channelData) =>
            Array.isArray(channelData?.records) &&
            channelData.records.length > 0
        )
      );

    if (!hasGraphData(graphDataSets)) {
      toastWarning(TextConstants.TrendGraph.NoChannelDataInSpecifiedRange);
    }

    yield put(
      Actions.fetchGraphDataSuccess({
        data: graphDataSets,
      })
    );
    callback?.();
  } catch (e) {
    yield put(Actions.fetchGraphDataError(e));

    if (e.message === TextConstants.Common.FileExpired) {
      toastError(TextConstants.TrendGraph.TimeoutError);
    }
  } finally {
    yield delay(1000);
    yield put(Actions.fetchGraphDataCleanState());
  }
}

export function* watchTrendGraphSagas() {
  yield takeLeading(
    Actions.trendGraphActions.FETCH_TREND_GRAPH_DATA,
    fetchGraphDataSaga
  );
}

export default function* rootSaga() {
  yield all([fork(watchTrendGraphSagas)]);
}
